import { useEffect } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

export interface SideDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: number | string;
  loading?: boolean;
}

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 480,
    },
    display: 'flex',
    flexDirection: 'column',
  },
}));

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

const Content = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: 24,
});

const Footer = styled(Box)(({ theme }) => ({
  padding: '16px 24px',
  borderTop: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
  display: 'flex',
  gap: 12,
  justifyContent: 'flex-end',
}));

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  zIndex: 1,
});

export function SideDrawer({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  loading = false,
}: SideDrawerProps) {
  // Handle Escape key to close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  return (
    <StyledDrawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        ...(width && {
          '& .MuiDrawer-paper': {
            width: {
              xs: '100%',
              md: width,
            },
          },
        }),
      }}
      // Focus trap and accessibility
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
    >
      <Header>
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close drawer" sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </Header>

      <Content>{children}</Content>

      {footer && <Footer>{footer}</Footer>}

      {loading && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}
    </StyledDrawer>
  );
}
